import { cn } from "lib/cn";
import { trpc } from "lib/trpc";
import { Star } from "lucide-react";
import { useState } from "react";
import type { FavoriteTarget } from "@compass/shared/schema/favorite";

function formatCount(count: number): string {
    if (count >= 1000) return "1000+";
    if (count >= 100) return `${Math.floor(count / 100) * 100}+`;
    return String(count);
}

interface FavoriteButtonProps {
    target: FavoriteTarget;
    className?: string;
    size?: number;
    showCount?: boolean;
}

export const FavoriteButton = ({
    target,
    className,
    size = 22,
    showCount = true,
}: FavoriteButtonProps) => {
    // Optimistic state: when set, overrides server data until invalidation resolves
    const [optimistic, setOptimistic] = useState<{ favorited: boolean; count: number } | null>(
        null,
    );
    const utils = trpc.useUtils();

    const { data } = trpc.favorites.isFavorited.useQuery(target);

    const toggleMutation = trpc.favorites.toggle.useMutation({
        onMutate: () => {
            const current = optimistic ?? data ?? { favorited: false, count: 0 };
            setOptimistic({
                favorited: !current.favorited,
                count: Math.max(0, current.count + (current.favorited ? -1 : 1)),
            });
        },
        onError: () => {
            setOptimistic(null);
        },
        onSettled: async () => {
            await utils.favorites.isFavorited.invalidate(target);
            setOptimistic(null);
            void utils.favorites.getUserFavorites.invalidate();
            void utils.favorites.getTrending.invalidate();
        },
    });

    const isFavorited = optimistic?.favorited ?? data?.favorited ?? false;
    const count = optimistic?.count ?? data?.count ?? 0;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        toggleMutation.mutate(target);
    };

    return (
        <button
            onClick={handleClick}
            className={cn(
                "relative inline-flex items-center justify-center rounded-full p-1 transition-colors hover:bg-gray-100",
                isFavorited && "text-yellow-500",
                !isFavorited && "text-gray-400 hover:text-yellow-400",
                className,
            )}
            aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
            title={isFavorited ? "Remove from favorites" : "Add to favorites"}
        >
            <Star
                size={size}
                fill={isFavorited ? "currentColor" : "none"}
                strokeWidth={isFavorited ? 0 : 1.5}
            />
            {showCount && count > 0 && (
                <span
                    className="absolute -top-1 -right-1 flex min-w-[16px] items-center justify-center rounded-full bg-gray-800 px-1 text-[9px] leading-none font-semibold text-white"
                    style={{ height: "16px" }}
                >
                    {formatCount(count)}
                </span>
            )}
        </button>
    );
};
